import { nameInitials } from './person.js';

const LEGAL_SUFFIXES = new Set(['LTD', 'LIMITED', 'PLC', 'LLP', 'LLC', 'INC', 'CO', 'AND']);

export function companyInitials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((word) => word && !LEGAL_SUFFIXES.has(word.toUpperCase()));
  return nameInitials(words.join(' '));
}

const relativeLuminance = (hex: string): number | undefined => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return undefined;
  const [r, g, b] = [0, 2, 4].map((offset) => {
    const channel = parseInt(match[1].slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Brand colours are chosen by each company, so the monogram's text colour follows whichever has more contrast.
const prefersWhiteText = (brandColor: string): boolean | undefined => {
  const luminance = relativeLuminance(brandColor);
  if (luminance === undefined) return undefined;
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack;
};

export function monogramTextColor(brandColor: string): string | undefined {
  const white = prefersWhiteText(brandColor);
  if (white === undefined) return undefined;
  return white ? 'var(--ns-theme-white)' : 'var(--ns-theme-black)';
}

const SAFE_SVG_COLOUR = /^(#[0-9a-f]{3,8}|hsla?\([\d.,%\s]+\)|rgba?\([\d.,%\s]+\))$/i;
const MONOGRAM_FALLBACK_BACKGROUND = '#5b1bf5';

// Graph nodes draw on a canvas, so a logo-less company needs its monogram as an image rather than markup.
export function companyMonogramImage(name: string, background = ''): string {
  const fill = SAFE_SVG_COLOUR.test(background.trim()) ? background.trim() : MONOGRAM_FALLBACK_BACKGROUND;
  const textFill = prefersWhiteText(fill) === false ? '#000000' : '#ffffff';
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">' +
    `<rect width="64" height="64" fill="${fill}"/>` +
    `<text x="32" y="41" text-anchor="middle" font-family="Inter, sans-serif" font-size="24" font-weight="600" fill="${textFill}">${companyInitials(name) || '?'}</text>` +
    '</svg>';
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * A Companies House number as the registrar writes it: trimmed, upper case and, for an all-digit number, padded back
 * to eight digits (spreadsheets drop the leading zeros, so 07025392 comes back as 7025392). Null when it's blank.
 */
export function normaliseCompanyNumber(value: string | null | undefined): string | null {
  const number = (value ?? '').replace(/\s+/g, '').toUpperCase();
  if (!number) return null;
  return /^\d{1,7}$/.test(number) ? number.padStart(8, '0') : number;
}
